/**
 * Calendar days in Europe/Zurich.
 *
 * Streaks and due dates must flip at Swiss midnight, not UTC midnight —
 * a child practising at 23:30 in January has earned that day.
 */
const ZURICH = "Europe/Zurich";
const dayFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: ZURICH,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

export type Day = string; // YYYY-MM-DD

export function todayInZurich(nowMs: number = Date.now()): Day {
  return dayFormatter.format(new Date(nowMs));
}

/** Day arithmetic on the calendar label itself, so DST never shifts a day. */
export function addDays(day: Day, delta: number): Day {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const base = Date.UTC(y, m - 1, d) + delta * 86_400_000;
  const shifted = new Date(base);
  const yy = shifted.getUTCFullYear();
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(shifted.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

export function daysBetween(from: Day, to: Day): number {
  const [fy, fm, fd] = from.split("-").map(Number) as [number, number, number];
  const [ty, tm, td] = to.split("-").map(Number) as [number, number, number];
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/** ISO-8601 week label, e.g. "2026-W37". Used for the weekly leaderboard. */
export function isoWeek(day: Day): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d));
  // Thursday of the current ISO week determines the year and week number.
  const dayOfWeek = (date.getUTCDay() + 6) % 7; // Monday = 0
  date.setUTCDate(date.getUTCDate() - dayOfWeek + 3);
  const isoYear = date.getUTCFullYear();
  const firstThursday = new Date(Date.UTC(isoYear, 0, 4));
  const firstDayOfWeek = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayOfWeek + 3);
  const week = 1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

export function nowSeconds(nowMs: number = Date.now()): number {
  return Math.floor(nowMs / 1000);
}

/** Swiss display form of a calendar day. The API still sends YYYY-MM-DD. */
export function formatDayDe(day: Day): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return `${d}.${m}.${y}`;
}

/**
 * Weekday of a calendar day, 0 = Sunday, as JavaScript counts them. The name
 * itself is UI copy and stays in apps/web/src/copy (docs/COPY.md §3).
 */
export function weekdayIndex(day: Day): number {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/**
 * How near a day is, so the interface can name it. The words themselves are
 * UI copy (apps/web/src/copy), only the decision lives here -- it has an edge
 * that is easy to get wrong and deserves a test.
 *
 * "weekday" stops six days out on purpose: day seven carries the same weekday
 * name as today, and «Freitag» a week away reads like this Friday. A day in
 * the past is a plain date; the exam is over.
 */
export type DayNearness = "today" | "tomorrow" | "weekday" | "date";

export const NAMED_HORIZON_DAYS = 6;

export function dayNearness(day: Day, today: Day): DayNearness {
  const away = daysBetween(today, day);
  if (away === 0) return "today";
  if (away === 1) return "tomorrow";
  if (away > 1 && away <= NAMED_HORIZON_DAYS) return "weekday";
  return "date";
}

/**
 * How many days back a round sent late still counts for the day it was played
 * (issue #118): practised on the train on Monday, sent on Tuesday, Monday keeps
 * its streak day. Older rounds count for the earliest day still allowed.
 */
export const LATE_ROUND_DAYS = 2;

/**
 * When a round counts, from the device clock the client sent along. Never in
 * the future, never more than LATE_ROUND_DAYS calendar days back. A round
 * without `playedAt` (an older client, or sent right away) counts now.
 */
export function playedWhen(playedAt: number | null | undefined, now: number): { seconds: number; day: Day } {
  const today = todayInZurich(now * 1000);
  if (playedAt == null || playedAt >= now) return { seconds: now, day: today };
  const day = todayInZurich(playedAt * 1000);
  if (daysBetween(day, today) <= LATE_ROUND_DAYS) return { seconds: playedAt, day };
  return { seconds: now - LATE_ROUND_DAYS * 86_400, day: addDays(today, -LATE_ROUND_DAYS) };
}
